import {
    Avatar,
    Backdrop,
    Box,
    CircularProgress,
    Divider,
    IconButton,
    ListItemIcon,
    Menu,
    MenuItem
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { Logout } from '@mui/icons-material';
import { logout, selectCurrentUser } from '../../redux/slices/authSlice';
import { showAlert } from '../../redux/slices/appSlice';
import { URL } from '../layout/Layout';
import { useAppDispatch, useAppSelector } from '../../hooks/reduxHooks';
import { useLazyLogoutQuery } from '../../services/authApi';
import { useState } from 'react';
import DocumentosHabilitados from './components/DocumentosHabilitados';
import HistorialDocumentos from './components/HistorialDocumentos';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

const boxContainer = {
    display: 'inline-flex',
    '& svg': { color: 'var(--white)' },
    '& .user-data-container': {
        paddingRight: '12px',
        textAlign: 'right'
    }
};

const paperStyles = {
    elevation: 0,
    sx: {
        '& ul': {
            padding: '10px',
            '& li:hover': {
                backgroundColor: '#E6E6E6',
                borderRadius: '6px'
            }
        },
        overflow: 'visible',
        filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
        mt: 1.5,
        '& .MuiAvatar-root': {
            width: 32,
            height: 32,
            ml: -0.5,
            mr: 1
        },
        '&::before': {
            content: '""',
            display: 'block',
            position: 'absolute',
            top: 0,
            right: 14,
            width: 10,
            height: 10,
            bgcolor: 'background.paper',
            transform: 'translateY(-50%) rotate(45deg)',
            zIndex: 0
        }
    }
};

function UserCardHeader() {
    const user = useAppSelector(selectCurrentUser);
    const dispatch = useAppDispatch();
    const [lazyLogout, { isLoading: loadingLogout }] = useLazyLogoutQuery();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [openEnableDocsModal, setOpenEnableDocsModal] = useState<boolean>(false);
    const [openHistory, setOpenHistory] = useState<boolean>(false);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    const handleOpenHistory = () => setOpenHistory(true);

    const handleCloseHistory = () => setOpenHistory(false);

    const handleLogOut = () => {
        const logOut = async () => {
            const token = localStorage.getItem('token');

            const response: any = await lazyLogout({ authorization: token });

            if (!response?.error) {
                sessionStorage.removeItem('sesionid');
                localStorage.clear();
                dispatch(logout());
                window.location.replace(URL);
            } else {
                enqueueSnackbar('Error al cerrar sesión', { variant: 'error' });
            }
        };

        dispatch(
            showAlert({
                title: '¿Desea cerrar sesión?',
                cancelText: 'Cancelar',
                confirmText: 'Sí, cerrar sesión',
                confirmAction: logOut
            })
        );
    };

    return (
        <>
            <Box sx={{ ...boxContainer }}>
                <Box className="user-data-container">
                    <Box className="title">
                        {user?.nombre} {user?.apellido}
                    </Box>
                    <Box>{user?.cuil}</Box>
                </Box>
                <Box>
                    <Avatar aria-label="recipe" />
                </Box>
                <Box>
                    <IconButton
                        aria-label="more"
                        id="long-button"
                        aria-controls={open ? 'long-menu' : undefined}
                        aria-expanded={open ? 'true' : undefined}
                        aria-haspopup="true"
                        onClick={handleClick}
                    >
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                    <Menu
                        id="basic-menu"
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleCloseMenu}
                        MenuListProps={{
                            'aria-labelledby': 'basic-button'
                        }}
                        slotProps={{
                            paper: { ...paperStyles }
                        }}
                        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                    >
                        <MenuItem
                            onClick={() => {
                                handleCloseMenu();
                                setOpenEnableDocsModal(true);
                            }}
                        >
                            Ver documentos habilitados
                        </MenuItem>
                        <MenuItem
                            onClick={() => {
                                handleCloseMenu();
                                handleOpenHistory();
                            }}
                        >
                            Mi historial
                        </MenuItem>
                        <Divider />
                        <MenuItem
                            onClick={() => {
                                handleCloseMenu();
                                handleLogOut();
                            }}
                            sx={{ justifyContent: 'space-between' }}
                        >
                            Cerrar Sesión
                            <ListItemIcon sx={{ justifyContent: 'right' }}>
                                <Logout fontSize="small" />
                            </ListItemIcon>
                        </MenuItem>
                    </Menu>
                </Box>
            </Box>

            <DocumentosHabilitados
                openEnableDocsModal={openEnableDocsModal}
                setOpenEnableDocsModal={setOpenEnableDocsModal}
            />

            <HistorialDocumentos openHistory={openHistory} onClose={handleCloseHistory} />

            <Backdrop
                sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })}
                open={loadingLogout}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
        </>
    );
}

export default UserCardHeader;
